import { COUNTRY_ISO2, DEFAULT_COUNTRY_ISO2 } from '../features/geo/geoConstants';

/**
 * Phone catalog for the countries the platform operates in.
 *
 * The calling code is deliberately kept apart from the mask: a mask literal
 * that is itself a digit cannot be told apart from a typed digit when the field
 * is re-formatted, which used to push the number one position to the right on
 * every keystroke. The code is chosen by the operator and only joined with the
 * national number when the order is submitted.
 *
 * Adding a country is a matter of appending an entry here; `mask` is optional
 * and only groups the digits for readability.
 *
 * @type {Array<{iso2: string, code: string, label: string, flag: string, mask?: string}>}
 */
export const PHONE_COUNTRIES = [
  { iso2: COUNTRY_ISO2.CL, code: '56', label: 'Chile', flag: '🇨🇱', mask: '9 9999 9999' },
  // 10 digits: a 2-digit lada plus 8 for CDMX, Guadalajara and Monterrey,
  // which is where most volume sits. A 3-digit lada groups as 44 2123 4567,
  // off by one space but still readable.
  { iso2: COUNTRY_ISO2.MX, code: '52', label: 'México', flag: '🇲🇽', mask: '99 9999 9999' },
];

/**
 * Layout for countries listed without a grouping of their own: no separators,
 * capped at what E.164 leaves once the longest calling code is taken out.
 */
const DEFAULT_MASK = '9999999999999';

/**
 * Options for the calling code picker.
 *
 * `label` carries the country name so it can be searched by name or by code,
 * while the trigger only has room for the flag and the code.
 *
 * @type {Array<{value: string, label: string, shortLabel: string}>}
 */
export const PHONE_COUNTRY_OPTIONS = PHONE_COUNTRIES.map((country) => ({
  value: country.iso2,
  label: `${country.flag}  ${country.label}  +${country.code}`,
  shortLabel: `${country.flag} +${country.code}`,
}));

/**
 * @param {string} iso2
 * @returns {{iso2: string, code: string, label: string, mask: string}}
 */
export const getPhoneCountry = (iso2) =>
  PHONE_COUNTRIES.find((country) => country.iso2 === iso2) ||
  PHONE_COUNTRIES.find((country) => country.iso2 === DEFAULT_COUNTRY_ISO2) ||
  PHONE_COUNTRIES[0];

/**
 * Calling code an account starts out with, so the operator rarely has to touch
 * the selector.
 *
 * @param {string} iso2 - ISO2 country code of the signed-in account
 * @returns {string} ISO2 code present in the catalog
 */
export const resolvePhoneCountryIso2 = (iso2) => getPhoneCountry(iso2).iso2;

/**
 * @param {string} iso2
 * @returns {string}
 */
export const getPhoneMask = (iso2) => getPhoneCountry(iso2).mask || DEFAULT_MASK;

/**
 * Applies a digit mask, where each `9` consumes one digit and any other
 * character is a literal that only appears once there are digits to place.
 *
 * Literals must not be digits, or they become indistinguishable from typed
 * input when the masked value is fed back in.
 *
 * @param {string} value - Raw input, may already contain literals
 * @param {string} mask
 * @returns {string}
 */
export const applyDigitMask = (value, mask) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';

  let result = '';
  let digitIndex = 0;

  for (const maskChar of mask) {
    if (digitIndex >= digits.length) break;

    if (maskChar === '9') {
      result += digits[digitIndex];
      digitIndex += 1;
    } else {
      result += maskChar;
    }
  }

  return result;
};

/**
 * Formats the national part of a phone number for the given country. The
 * calling code is not part of the result.
 *
 * @param {string} value - Current field value, already formatted or raw
 * @param {string} iso2
 * @returns {string}
 */
export const formatNationalPhone = (value, iso2) => applyDigitMask(value, getPhoneMask(iso2));

/**
 * How many digits a mask can hold.
 *
 * @param {string} mask
 * @returns {number}
 */
const countMaskDigits = (mask) => mask.split('').filter((char) => char === '9').length;

/**
 * Whether the national number carries as many digits as its country expects.
 *
 * A half-typed phone is a failed delivery, so it is worth catching in the form.
 * Countries listed without a mask have no known length and are accepted as soon
 * as they carry digits.
 *
 * @param {string} national
 * @param {string} iso2
 * @returns {boolean}
 */
export const isCompletePhone = (national, iso2) => {
  const digits = String(national || '').replace(/\D/g, '');
  const { mask } = getPhoneCountry(iso2);

  if (!mask) return digits.length > 0;
  return digits.length === countMaskDigits(mask);
};

/**
 * Reads what the operator put in the national field, which may be a pasted
 * number that still carries its calling code, e.g. `+56 9 1234 5678`.
 *
 * The code is only split off when the digits overflow the mask, so a number
 * typed one digit at a time is never second-guessed. A pasted code belonging to
 * another country also moves the selector.
 *
 * @param {string} value
 * @param {string} currentIso2 - Country currently chosen in the selector
 * @returns {{iso2: string, national: string}}
 */
export const parsePhoneEntry = (value, currentIso2) => {
  const digits = String(value || '').replace(/\D/g, '');
  const current = getPhoneCountry(currentIso2);

  if (digits.length > countMaskDigits(getPhoneMask(current.iso2))) {
    const carried = PHONE_COUNTRIES.find(
      (country) =>
        digits.startsWith(country.code) &&
        digits.length - country.code.length <= countMaskDigits(getPhoneMask(country.iso2))
    );

    if (carried) {
      return {
        iso2: carried.iso2,
        national: formatNationalPhone(digits.slice(carried.code.length), carried.iso2),
      };
    }
  }

  return { iso2: current.iso2, national: formatNationalPhone(digits, current.iso2) };
};

/**
 * Joins the calling code and the national number into the single E.164-style
 * string the backend stores, e.g. `+56911111111`.
 *
 * The mask exists only to make the field readable while it is typed; the
 * separators are dropped here so what travels is plain digits behind a `+`.
 *
 * @param {string} iso2
 * @param {string} national - Masked national number
 * @returns {string} Empty when there is no national number
 */
export const composePhone = (iso2, national) => {
  const digits = String(national || '').replace(/\D/g, '');
  if (!digits) return '';
  return `+${getPhoneCountry(iso2).code}${digits}`;
};

/**
 * The inverse of `composePhone`: splits a stored number back into the country
 * and the masked national part the composite field edits.
 *
 * Stored numbers are not uniform — older records were saved without the `+`,
 * and some with spaces — so this works off the digits alone. A calling code is
 * only split off when what remains fits that country's mask, which stops a
 * local number that happens to start with `52` from being read as Mexico.
 *
 * @param {string} value - Stored phone, e.g. `+56911111111` or `56912312313`
 * @param {string} fallbackIso2 - Country to assume when no code is recognised
 * @returns {{iso2: string, national: string}}
 */
export const splitPhone = (value, fallbackIso2) => {
  const digits = String(value || '').replace(/\D/g, '');
  const fallback = getPhoneCountry(fallbackIso2);

  if (!digits) return { iso2: fallback.iso2, national: '' };

  const match = PHONE_COUNTRIES.find(
    (country) =>
      digits.startsWith(country.code) &&
      digits.length - country.code.length === countMaskDigits(getPhoneMask(country.iso2))
  );

  if (match) {
    return {
      iso2: match.iso2,
      national: formatNationalPhone(digits.slice(match.code.length), match.iso2),
    };
  }

  return { iso2: fallback.iso2, national: formatNationalPhone(digits, fallback.iso2) };
};
